import {
  AddCard,
  Delete,
  Edit,
  Paid,
  Savings,
  SwapHoriz,
} from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { editCash as callEditCash } from '../../../../http/PortfoliosApi';
import DeletePortfolioDialog from '../../dialogs/DeletePortfolioDialog';
import ContributionsListComponent from './ContributionListComponent';
import DividendsListComponent from './DividendsListComponent';
import TransactionsListComponent from './TransactionsListComponent';
import EditCashDialog from './dialogs/EditCashDialog';

export default function PortfolioManagementComponent({
  contributions,
  loadPortfolio,
  page,
  portfolio,
  portfolios,
  setPortfolios,
  reloadContributions,
  setPage,
  transactions,
  reloadTransactions,
}) {
  const [editCashDialogOpen, setEditCashDialogOpen] = React.useState(false);
  const [deletePortfolioDialogOpen, setDeletePortfolioDialogOpen] =
    React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [errMessage, setErrMessage] = React.useState('An error happened!');

  const amountFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  });

  const editCash = async ({ value }) => {
    await callEditCash(portfolio?.id, { cash: value });
    await loadPortfolio();
  };

  const KPICard = ({
    title,
    value,
    icon,
    action = null,
    color = 'primary.main',
  }) => (
    <Card sx={{ height: '100%', borderTop: `4px solid`, borderColor: color }}>
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color, paddingLeft: '20px' }}>{icon}</Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
            >
              {title}
            </Typography>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
          </Box>
          {action}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        px: isMobile ? 0 : 3,
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <EditCashDialog
        editCash={editCash}
        editCashDialogOpen={editCashDialogOpen}
        setEditCashDialogOpen={setEditCashDialogOpen}
        errMessage={errMessage}
        portfolio={portfolio}
        setErrMessage={setErrMessage}
        setSnackOpen={setSnackOpen}
      />

      <Grid
        container
        rowSpacing={3}
        columnSpacing={isMobile ? 0 : 3}
        mb={4}
      >
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Contributions"
            value={`${contributions?.count || 0} | ${amountFormatter.format(
              contributions?.sum ?? 0,
            )}`}
            icon={<AddCard />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Cash Available"
            value={amountFormatter.format(portfolio?.cash)}
            icon={<Savings />}
            color="success.main"
            action={
              <IconButton
                size="small"
                onClick={() => setEditCashDialogOpen(true)}
              >
                <Edit fontSize="small" />
              </IconButton>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Returns"
            value={amountFormatter.format(portfolio?.state?.roicEUR || 0)}
            icon={<Paid />}
            color={
              portfolio?.state?.roicEUR >= 0 ? 'success.main' : 'error.main'
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Transactions"
            value={`${transactions?.metadata.count || 0} | ${
              transactions.metadata.yearlyAvg
            } tx/year`}
            icon={<SwapHoriz />}
            color="info.main"
          />
        </Grid>
      </Grid>

      <Grid container rowSpacing={4} columnSpacing={isMobile ? 0 : 4}>
        <Grid item xs={12} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 'medium' }}
            >
              Contributions History
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ContributionsListComponent
              portfolio={portfolio}
              contributions={contributions}
              reloadContributions={reloadContributions}
              loadPortfolio={loadPortfolio}
              errMessage={errMessage}
              setErrMessage={setErrMessage}
              setSnackOpen={setSnackOpen}
              page={page}
              setPage={setPage}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={9}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 'medium' }}
            >
              Transaction Ledger
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TransactionsListComponent
              portfolio={portfolio}
              transactions={transactions}
              reloadTransactions={reloadTransactions}
              loadPortfolio={loadPortfolio}
              errMessage={errMessage}
              setErrMessage={setErrMessage}
              setSnackOpen={setSnackOpen}
              page={page}
              setPage={setPage}
              onEditCash={() => setEditCashDialogOpen(true)}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          color="text.secondary"
          sx={{ fontWeight: 'medium' }}
        >
          Dividends
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <DividendsListComponent
          portfolio={portfolio}
          errMessage={errMessage}
          setErrMessage={setErrMessage}
          setSnackOpen={setSnackOpen}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.light',
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          color="error"
          sx={{ fontWeight: 'medium' }}
        >
          Danger zone
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Button
          onClick={() => setDeletePortfolioDialogOpen(true)}
          variant="outlined"
          endIcon={<Delete />}
          color="error"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Delete portfolio
        </Button>
      </Paper>

      <DeletePortfolioDialog
        activePortfolioId={portfolio?.id}
        portfolioName={portfolio?.name}
        setPortfolios={setPortfolios}
        deletePortfolioDialogOpen={deletePortfolioDialogOpen}
        setDeletePortfolioDialogOpen={setDeletePortfolioDialogOpen}
        portfolios={portfolios}
        errMessage={errMessage}
        setErrMessage={setErrMessage}
        setSnackOpen={setSnackOpen}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert severity="error">{errMessage}</Alert>
      </Snackbar>
    </Box>
  );
}
